import { EFileTypes } from '@common/definitions';

const EFileTypeRegexes = {
  IMAGE: /\.(jpe?g|png|bmp|svg)$/i,
  PDF: /\.(pdf)$/i,
  VIDEO: /\.(mp4|mov|avi|m4v|3gp|qt|mpe?g)/i,
  AUDIO: /\.(wav|mp3|aac|webm|ogg|oga|m4a)$/i,
  DOCUMENT: /\.(doc|docx|docm|dotx|dotm|pages|csv|txt)$/i,
  SPREADSHEET: /\.(xls|xlsx|xlsm|xltx|xltm|numbers)$/i,
  PRESENTATION: /\.(ppt|pptx|pps|ppsx|pptm|potx|potm|key|odp)$/i,
  GIF: /\.(gif|x-xbitmap|gi_)$/i,
};

export const getFileTypeFromPath = (path: string) => {
  if (EFileTypeRegexes.IMAGE.test(path)) return EFileTypes.IMAGE;
  if (EFileTypeRegexes.GIF.test(path)) return EFileTypes.GIF;
  if (EFileTypeRegexes.VIDEO.test(path)) return EFileTypes.VIDEO;
  if (EFileTypeRegexes.AUDIO.test(path)) return EFileTypes.AUDIO;
  if (EFileTypeRegexes.PDF.test(path)) return EFileTypes.PDF;
  if (EFileTypeRegexes.DOCUMENT.test(path)) return EFileTypes.DOCUMENT;
  if (EFileTypeRegexes.SPREADSHEET.test(path)) return EFileTypes.SPREADSHEET;
  if (EFileTypeRegexes.PRESENTATION.test(path)) return EFileTypes.PRESENTATION;

  return EFileTypes.UNKNOWN;
};

const KNOWN_FILE_TYPES = new Set<string>([
  EFileTypes.IMAGE,
  EFileTypes.GIF,
  EFileTypes.VIDEO,
  EFileTypes.PDF,
  EFileTypes.AUDIO,
  EFileTypes.DOCUMENT,
  EFileTypes.SPREADSHEET,
  EFileTypes.PRESENTATION,
  EFileTypes.EXTERNAL_VIDEO,
  EFileTypes.UNKNOWN,
]);

export const getResolvedFileType = (
  attachment: {
    file_type?: string | null;
    file_name?: string | null;
    path?: string | null;
  }
): string => {
  const { file_type, file_name, path } = attachment;
  const fromPath = getFileTypeFromPath(file_name || path || '');

  // Voice notes are often stored as audio/webm (or .webm classified as video/document).
  if (file_type?.startsWith('audio/') || fromPath === EFileTypes.AUDIO) {
    return EFileTypes.AUDIO;
  }
  if (file_type?.startsWith('image/')) return EFileTypes.IMAGE;
  if (file_type?.startsWith('video/')) return EFileTypes.VIDEO;
  if (file_type && KNOWN_FILE_TYPES.has(file_type)) return file_type;
  return fromPath;
};

/** Parse `voice-{timestamp}-{durationMs}.ext` written by createVoiceFile. */
export function parseVoiceDurationSeconds(fileName?: string | null): number | undefined {
  if (!fileName) return undefined;
  const match = fileName.match(/voice-\d+-(\d+)\.(?:webm|ogg|oga|m4a)/i);
  if (!match) return undefined;
  const ms = Number(match[1]);
  if (!Number.isFinite(ms) || ms <= 0) return undefined;
  return ms / 1000;
}

export function isVoiceNoteFileName(fileName?: string | null): boolean {
  return !!fileName && /voice-\d+-\d+\.(?:webm|ogg|oga|m4a)/i.test(fileName);
}
